import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { isToday } from 'date-fns';
import { ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '~/ui/components/common/Button';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import PrivateImage from '~/ui/components/common/PrivateImage';

import useRole from '~/store/user/hooks/useRole';
import useFormStorage from '../hooks/useFormStorage';

import { IClientAllocationDetailed } from '~/services/api/clientAllocation/types';
import { EDIT_DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';
import { IUserRole } from '~/types';
import { NETWORK } from '~/ui/constants/paths';
import { getViewAllocationPath } from '../helpers/getLocationPath';

import AllocationClientsList from './components/AllocationClientsList';
import { getMemoByClientAndAllocation, normalizeAllocationsByUser } from './utils';

import { ReactComponent as GlobeIcon } from '~/ui/assets/images/global.svg';
import smallAvatarIcon from '~/ui/assets/images/smallAvatar.svg';
import { EDIT_PAST_ALLOCATION_DESCRIPTION, LEAVE_PAGE_DESCRIPTION } from './constants';

import { useStoreActions } from '~/store/hooks';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import { getLocalDateNoFormat } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';
import useHandleAccordion from './hooks/useHandleAccordion';
import { IFormValues } from './types';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './EditCrisisAllocation.module.scss';

interface IProps {
  clientAllocations: IClientAllocationDetailed[];
  actTeamId: string;
  clinicId: string;
}

const EditCrisisAllocationForm = ({
  clientAllocations,
  actTeamId,
  clinicId,
}: IProps): ReactElement => {
  const [isAdding, setIsAdding] = useState(false);
  // * details used to transfer data from form to modal
  const [details, setDetails] = useState<IFormValues | null>(null);
  const [withClearDraft, setWithClearDraft] = useState(false);
  const { expandedMemberId, handleChange } = useHandleAccordion();

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onEditCrisisAllocation, onDeleteCrisisAllocation } = useStoreActions(
    actions => actions.crisis,
  );

  const navigate = useNavigate();

  const { isActTeam, isGlobalUser } = useRole();

  const allocationDate = clientAllocations[0]?.date;

  const defValues: IFormValues = {
    date: allocationDate,
    allocations: normalizeAllocationsByUser(clientAllocations),
    changedAllocationsIds: [],
    deletedAllocationsIds: [],
  };

  const form = useForm<IFormValues>({
    values: defValues,
  });
  const {
    formState: { errors, isDirty },
    watch,
    handleSubmit,
  } = form;
  const [allocations, changedAllocationsIds, deletedAllocationsIds] = watch([
    'allocations',
    'changedAllocationsIds',
    'deletedAllocationsIds',
  ]);

  const hasAllAllocationsResolved =
    clientAllocations.length > 0 ? clientAllocations.every(item => !!item.resolved) : false;

  const { isRestored, clearDraft } = useFormStorage(
    form,
    {
      key: EDIT_DRAFT_ALLOCATION_KEY,
      conditionKey: 'date',
    },
    defValues,
  );

  const onSubmit = async (values: IFormValues) => {
    setIsAdding(true);
    try {
      if (!deletedAllocationsIds.length && !changedAllocationsIds.length) {
        showError('No allocations to update');
        setIsAdding(false);
        return;
      }

      if (deletedAllocationsIds.length) {
        await Promise.all(
          deletedAllocationsIds.map(async allocationId => {
            await onDeleteCrisisAllocation({
              clinicId,
              id: allocationId,
            });
          }),
        );
      }
      if (changedAllocationsIds.length) {
        await Promise.all(
          changedAllocationsIds.map(async updatedAllocationId => {
            const updatedAllocation = clientAllocations.find(
              alloc => alloc.id === updatedAllocationId,
            );
            updatedAllocation?.user.clients.forEach(client => {
              onEditCrisisAllocation({
                clinicId,
                id: String(updatedAllocation.id),
                memo: getMemoByClientAndAllocation(
                  values.allocations,
                  client.id,
                  updatedAllocation.id,
                  updatedAllocation.user.id,
                ),
              });
            });
          }),
        );
      }
      showNotify({ message: 'Client allocation successfully updated' });
      navigate(NETWORK);
      clearDraft();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsAdding(false);
    }
  };

  const openConfirmationModal = (values: IFormValues) => {
    const { date } = values;
    if (getLocalDateNoFormat(date) < new Date() && !isToday(getLocalDateNoFormat(date))) {
      setDetails(values);
      return;
    }
    onSubmit(values);
  };

  const handleCancel = () => {
    setWithClearDraft(true);

    if (isGlobalUser || isActTeam) {
      const finalRoute = getViewAllocationPath(true, isGlobalUser)
        .replace(':actTeamId', actTeamId)
        .replace(':clientAllocationDate', allocationDate.split('T')[0]);
      navigate(finalRoute);
    } else navigate(NETWORK);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(openConfirmationModal)}>
        <Paper>
          <Box p={2}>
            <Grid container spacing={4}>
              <Grid item sm={12}>
                <Typography variant="subtitle1" className={styles.assignedClientsTitle}>
                  Assigned Clients per Team Member
                </Typography>
              </Grid>
            </Grid>

            {isRestored && (
              <div className={styles.formWrapper}>
                {allocations.map((allocation, allocationIdx) => {
                  const hasGlobalUserRole = !!allocation.roles
                    ?.filter(r => r.clinic.id === Number(clinicId))
                    .find(r => r.name === IUserRole.GlobalUser);

                  const expanded = expandedMemberId === String(allocation.userId);

                  return (
                    <div key={allocation.id}>
                      <Accordion
                        className={styles.accordion}
                        TransitionProps={{ unmountOnExit: true }}
                        expanded={expanded}
                        onChange={handleChange(String(allocation.userId))}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1a-content"
                          id="panel1a-header"
                        >
                          <Grid container>
                            <Grid item xs={4} className={styles.accordionSummaryItemName}>
                              <div className={styles.photoContainer}>
                                <PrivateImage
                                  className={styles.image}
                                  src={allocation.photo || smallAvatarIcon}
                                  alt="avatar"
                                />
                                {hasGlobalUserRole && (
                                  <div className={styles.absoluteIcon}>
                                    <GlobeIcon
                                      width={16}
                                      height={16}
                                      color={variables.colorGreenGlobal}
                                    />
                                  </div>
                                )}
                              </div>
                              <Typography>{allocation.name}</Typography>
                            </Grid>

                            <Grid item xs={4} className={styles.accordionSummaryItemCenter}>
                              <Typography>{allocation.clients.length} Allocated Clients</Typography>
                            </Grid>

                            <Grid item xs={4} className={styles.accordionSummaryItemRight}>
                              <Typography>Details</Typography>
                            </Grid>
                          </Grid>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Grid container spacing={3}>
                            <Grid item sm={12}>
                              <AllocationClientsList allocationIdx={allocationIdx} />
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  );
                })}
              </div>
            )}
          </Box>
        </Paper>

        <div className={styles.buttons}>
          <div className={styles.buttonWrapper}>
            <Button color="primary" variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={
              Object.keys(errors).length > 0 ||
              hasAllAllocationsResolved ||
              (!deletedAllocationsIds.length && !changedAllocationsIds.length)
            }
          >
            Save Changes
          </Button>
        </div>

        <NavigationConfirmModal
          when={changedAllocationsIds.length > 0 && isDirty && !isAdding}
          description={LEAVE_PAGE_DESCRIPTION}
          beforeConfirm={withClearDraft ? clearDraft : undefined}
        />
        {/* this modal triggers only when allocation date is in the past */}
        {details && (
          <ConfirmModal
            onConfirm={() => {
              onSubmit(details);
            }}
            onClose={() => setDetails(null)}
            description={EDIT_PAST_ALLOCATION_DESCRIPTION}
            confirmText="Proceed"
          />
        )}
      </form>
    </FormProvider>
  );
};

export default EditCrisisAllocationForm;
